import { Link } from 'react-router-dom';
import { Badge, Box, Button, Container, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { IconChartLine, IconCircleCheckFilled, IconExternalLink } from '@tabler/icons-react';

import { Reveal } from '@/components/motion/Reveal';
import classes from './AnalyzerPromo.module.css';

const BULLETS = [
  'Auto-detects AFR, knock, ignition timing, fuel trims',
  'Side-by-side compare against your base map',
  'Anomaly tagging — knock events, lean spikes, ignition retard',
  'Export PDF reports for customers or your own records',
];

export function AnalyzerPromo() {
  return (
    <Box component="section" id="analyzer" py={{ base: 64, md: 96 }} className={classes.wrap} aria-labelledby="analyzer-title">
      <Container size="xl">
        <Grid gap={{ base: 'xl', md: 64 }} align="center">
          <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 2, md: 1 }}>
            <Stack gap="md">
              <Reveal>
                <Badge
                  variant="outline"
                  color="ai"
                  size="lg"
                  radius="sm"
                  leftSection={<IconChartLine size={14} />}
                  styles={{
                    root: {
                      borderColor: 'rgba(187, 134, 252, 0.4)',
                      color: 'var(--tf-ai)',
                      background: 'rgba(187, 134, 252, 0.06)',
                    },
                  }}
                >
                  Clancy Analyzer
                </Badge>
              </Reveal>
              <Reveal delay={0.05}>
                <Title id="analyzer-title" order={2} className={classes.heading}>
                  Drag in a datalog. Get a tuning brief in 30 seconds.
                </Title>
              </Reveal>
              <Reveal delay={0.1}>
                <Text c="var(--tf-text-secondary)" lh={1.6}>
                  The Analyzer ingests PCM logs, AEM CSVs, and HP Tuners VCM Scanner exports. It
                  highlights what changed, what&rsquo;s drifting, and where the AI advisor would
                  recommend a closer look — without leaving the browser.
                </Text>
              </Reveal>

              <Stack gap="xs" mt="sm">
                {BULLETS.map((b, idx) => (
                  <Reveal key={b} delay={0.15 + idx * 0.05}>
                    <Group gap="sm" align="center" wrap="nowrap">
                      <IconCircleCheckFilled
                        size={18}
                        color="var(--tf-safety)"
                        style={{ flexShrink: 0 }}
                      />
                      <Text size="sm" c="var(--tf-text-secondary)">
                        {b}
                      </Text>
                    </Group>
                  </Reveal>
                ))}
              </Stack>

              <Reveal delay={0.35}>
                <Group gap="md" mt="md">
                  <Button
                    component={Link}
                    to="/analyzer"
                    size="md"
                    radius="md"
                    variant="gradient"
                    gradient={{ from: 'var(--tf-forge-start)', to: 'var(--tf-forge-end)', deg: 135 }}
                    rightSection={<IconExternalLink size={16} />}
                  >
                    Open the Analyzer
                  </Button>
                </Group>
              </Reveal>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 1, md: 2 }}>
            <Reveal delay={0.1}>
              <AnalyzerPreview />
            </Reveal>
          </Grid.Col>
        </Grid>
      </Container>
    </Box>
  );
}

/** Decorative inline SVG of a datalog chart — no asset deps, dark-mode ready. */
function AnalyzerPreview() {
  return (
    <Box className={classes.previewCard} role="img" aria-label="Datalog chart preview">
      <svg viewBox="0 0 480 280" width="100%" height="auto" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="afr-grad" x1="0" y1="0" x2="0" y2="280">
            <stop offset="0" stopColor="#FF6B2C" stopOpacity="0.35" />
            <stop offset="1" stopColor="#FF6B2C" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="knock-grad" x1="0" y1="0" x2="0" y2="280">
            <stop offset="0" stopColor="#BB86FC" stopOpacity="0.25" />
            <stop offset="1" stopColor="#BB86FC" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* grid */}
        {[40, 80, 120, 160, 200, 240].map((y) => (
          <line
            key={y}
            x1="20"
            x2="460"
            y1={y}
            y2={y}
            stroke="#2A2A3A"
            strokeWidth="1"
            strokeDasharray="3 5"
          />
        ))}
        {/* AFR area */}
        <path
          d="M20,180 L60,150 L100,170 L140,140 L180,155 L220,120 L260,100 L300,115 L340,90 L380,110 L420,80 L460,95 L460,280 L20,280 Z"
          fill="url(#afr-grad)"
        />
        <path
          d="M20,180 L60,150 L100,170 L140,140 L180,155 L220,120 L260,100 L300,115 L340,90 L380,110 L420,80 L460,95"
          fill="none"
          stroke="#FF8F4C"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Knock dotted overlay */}
        <path
          d="M20,200 L60,210 L100,205 L140,215 L180,200 L220,195 L260,180 L300,170 L340,165 L380,150 L420,160 L460,145"
          fill="url(#knock-grad)"
          opacity="0.4"
        />
        <path
          d="M20,200 L60,210 L100,205 L140,215 L180,200 L220,195 L260,180 L300,170 L340,165 L380,150 L420,160 L460,145"
          fill="none"
          stroke="#BB86FC"
          strokeWidth="1.5"
          strokeDasharray="4 4"
        />
        {/* anomaly marker */}
        <circle cx="260" cy="100" r="6" fill="#FF1744" />
        <circle cx="260" cy="100" r="11" fill="none" stroke="#FF1744" strokeWidth="1.5" opacity="0.6" />
        <text x="278" y="98" fill="#FF1744" fontFamily="JetBrains Mono, monospace" fontSize="11">
          Knock @ 4800 RPM
        </text>
      </svg>
      <Group justify="space-between" mt="sm" px="xs">
        <Text size="xs" c="var(--tf-text-muted)" ff="monospace">
          datalog_2026-04-12.csv
        </Text>
        <Group gap="xs">
          <Box className={classes.legendDot} style={{ background: '#FF8F4C' }} />
          <Text size="xs" c="var(--tf-text-secondary)">
            AFR
          </Text>
          <Box className={classes.legendDot} style={{ background: '#BB86FC' }} />
          <Text size="xs" c="var(--tf-text-secondary)">
            Knock
          </Text>
        </Group>
      </Group>
    </Box>
  );
}
